import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { ChevronRight, Users, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { tableLabel, getSections } from "@/lib/tables";

export default function WaiterDashboard() {
    const navigate = useNavigate();
    const location = useLocation();
    const isKitchenFlow = location.pathname.startsWith("/mutfak");
    const basePath = isKitchenFlow ? "/mutfak/masa" : "/garson/masa";
    const [sections, setSections] = useState(() => getSections());
    const [orders, setOrders] = useState([]);

    useEffect(() => {
        const refresh = () => setSections(getSections());
        window.addEventListener("tables-updated", refresh);
        return () => window.removeEventListener("tables-updated", refresh);
    }, []);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/orders");
            setOrders(data);
        } catch (e) {
            toast.error(formatError(e));
        }
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(load, 4000);
        return () => clearInterval(id);
    }, [load]);

    const orderByTable = Object.fromEntries(orders.map((o) => [o.table_number, o]));
    const occupiedCount = orders.length;

    return (
        <div className="min-h-screen bg-[#FAF9F6]">
            <AppHeader
                title={isKitchenFlow ? "Mutfak · Yeni Sipariş" : "Garson Paneli"}
                right={
                    <div className="flex items-center gap-3">
                        {isKitchenFlow && (
                            <button
                                onClick={() => navigate("/mutfak")}
                                data-testid="back-to-kitchen"
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-[#2C302E] bg-[#F3F1EB] hover:bg-[#E5E3D8]"
                            >
                                <ArrowLeft className="w-4 h-4" /> Mutfağa dön
                            </button>
                        )}
                        <div className="hidden md:flex items-center gap-2 text-sm text-[#59605B] px-4 py-2 rounded-lg bg-[#F3F1EB]" data-testid="occupied-count">
                            <Users className="w-4 h-4" />
                            {occupiedCount} dolu masa
                        </div>
                    </div>
                }
            />
            <main className="max-w-[1600px] mx-auto px-6 md:px-8 py-8">
                <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#59605B] mb-2">Salon</div>
                        <h1 className="font-display text-3xl sm:text-4xl font-semibold text-[#2C302E]">Masalar</h1>
                        <p className="text-sm text-[#59605B] mt-2">Sipariş almak için bir masa seçin.</p>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                        <span className="inline-flex items-center gap-2 text-[#59605B]">
                            <span className="w-3 h-3 rounded-full bg-white border border-[#E5E3D8]" />
                            Boş
                        </span>
                        <span className="inline-flex items-center gap-2 text-[#59605B]">
                            <span className="w-3 h-3 rounded-full bg-[#D96C4A]" />
                            Dolu
                        </span>
                    </div>
                </div>

                <div className="space-y-8">
                {sections.map((section) => (
                <div key={section.label}>
                    <div className="text-sm font-semibold uppercase tracking-[0.2em] text-[#2C302E] mb-3">
                        {section.label}
                    </div>
                    <div className="space-y-5">
                    {section.groups.map((g) => (
                    <div key={g.label}>
                        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#59605B] mb-2">
                            {g.label}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                            {Array.from({ length: g.to - g.from + 1 }, (_, i) => i + g.from).map((n) => {
                                const order = orderByTable[n];
                                const occupied = !!order;
                                const total = order
                                    ? order.items.reduce((s, it) => s + it.price * it.qty, 0)
                                    : 0;
                                return (
                                    <button
                                        key={n}
                                        onClick={() => navigate(`${basePath}/${n}`)}
                                        data-testid={`table-${n}-card`}
                                        className={`group relative text-left aspect-square flex flex-col justify-between p-4 rounded-2xl border transition-all hover:-translate-y-1 hover:shadow-lg ${
                                            occupied
                                                ? "bg-[#D96C4A]/5 border-2 border-[#D96C4A]"
                                                : "bg-white border-[#E5E3D8] hover:border-[#D96C4A]/40"
                                        }`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#59605B]">{g.label}</div>
                                                <div className="font-display text-3xl font-semibold text-[#2C302E]">{n - g.from + 1}</div>
                                            </div>
                                            <ChevronRight className="w-5 h-5 text-[#59605B] opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </div>
                                        <div>
                                            {occupied ? (
                                                <div className="space-y-1">
                                                    <div className="text-xs text-[#59605B]">
                                                        {(() => {
                                                            const totalQ = order.items.reduce((s, i) => s + i.qty, 0);
                                                            const sentQ = order.items.reduce((s, i) => s + (i.sent_qty || 0), 0);
                                                            return sentQ >= totalQ
                                                                ? `✓ ${totalQ}/${totalQ} masada`
                                                                : sentQ > 0
                                                                    ? `${sentQ}/${totalQ} gitti`
                                                                    : `${totalQ} ürün mutfakta`;
                                                        })()}
                                                    </div>
                                                    <div className="font-display text-lg font-semibold text-[#D96C4A]">
                                                        ₺{total.toFixed(2)}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="text-xs text-[#59605B]">Boş</div>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    ))}
                    </div>
                </div>
                ))}
                </div>
            </main>
        </div>
    );
}
